import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { CONSTANTS } from 'src/Core/Constant';
import { environment } from 'src/environments/environment';
import { TranslationService } from 'src/app/Service/translation.service';

@Component({
  selector: 'app-skills',
  templateUrl: './skills.component.html',
  styleUrls: ['./skills.component.scss']
})
export class SkillsComponent {



  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();
  @Input() isPreviewMode: boolean = false; // Flag to determine behavior
  @Input() applicantData: any; // Accept data from parent component
  isEditable: boolean = true; // Flag to track if the form is editable


  
  skillsForm!: FormGroup;
  submitted = false;
  SaveMode = CONSTANTS.SAVE_MODE;
  isJsonShow = environment.isJsonShow;  // Set isJsonShow from the environment

  skillCategories: any[] = [];
  skillRatings = [
    { id: 1, labelKey: 'skills.level.excellent' },
    { id: 2, labelKey: 'skills.level.good' },
    { id: 3, labelKey: 'skills.level.basic' },
    { id: 4, labelKey: 'skills.level.none' }
  ];
  readonly maxSkillLevelBlocks: number = 4;
  expandedCategories: Set<number> = new Set(); // Track expanded categories


  constructor(private activatedRoute: ActivatedRoute, private router: Router, private fb: FormBuilder, private toastr: ToastrService, private api: SevaProfileService, public genericValidations: GenericValidationsService, private translationService: TranslationService) {
  }


  ngOnInit(): void {
    this.initializeForm();
    if (!this.isPreviewMode) {
      this.getApplicantData();
    }
  }



ngOnChanges(changes: SimpleChanges): void {
  if (changes['applicantData'] && this.applicantData) {
    if (this.applicantData.Data && this.applicantData.Data.Categories) {
      this.skillCategories = this.applicantData.Data.Categories;
      this.buildDynamicForm(this.skillCategories);
      this.skillsForm.patchValue(this.applicantData.Data);
    }
    this.skillsForm.disable();
  }
}



      
  getApplicantData(){
    this.api.apiCall('Skills/GetUserSkills', 'GET').subscribe((response: any) => {
      if (response.Data && response.Data.Categories && response.Data.Categories.length > 0) {
        this.skillCategories = response.Data.Categories;
        this.buildDynamicForm(this.skillCategories);
        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage || 0);
        
        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided

        // Disable form if not editable
        if (!this.isEditable) {
          this.skillsForm.disable();
        } else {
          this.skillsForm.enable();
        }
        
        this.skillsForm.patchValue(response.Data);
      }
    })
  }

  
  initializeForm(){
    this.skillsForm = this.fb.group({
      Categories: this.fb.array([]),
      SaveMode:['', Validators.required],
    });
  }

  get categories(): FormArray {
    return this.skillsForm.get('Categories') as FormArray;
  }

  getSkillItems(categoryIndex: number): FormArray {
    return this.categories.at(categoryIndex).get('SkillItems') as FormArray;
  }

  getSkillLevelBlocks(levelIndex: number): number[] {
    // Higher proficiency levels (earlier in the list) show more blocks
    const blockCount = this.maxSkillLevelBlocks - levelIndex;
    return Array.from({ length: blockCount }, (_, idx) => idx);
  }

  // Get current rating object based on rating ID
  getCurrentRating(ratingId: number): any {
    if (ratingId === 0) {
      return this.skillRatings[this.skillRatings.length - 1]; // Return None (id: 4)
    }
    return this.skillRatings.find(r => r.id === ratingId) || this.skillRatings[this.skillRatings.length - 1];
  }

  // Get number of filled blocks to display based on rating ID
  // Returns array with count of filled blocks: Excellent(4) -> Good(3) -> Basic(2) -> None(0)
  getBlocksForRating(ratingId: number): number[] {
    if (ratingId === 0 || ratingId === 4) {
      return []; // None shows no filled blocks
    }
    const rating = this.getCurrentRating(ratingId);
    const levelIndex = this.skillRatings.findIndex(r => r.id === rating.id);
    const blockCount = this.maxSkillLevelBlocks - levelIndex;
    return Array.from({ length: blockCount }, (_, idx) => idx);
  }

  // Get total number of blocks to display (always 4 for visual consistency)
  getTotalBlocks(): number[] {
    return Array.from({ length: this.maxSkillLevelBlocks }, (_, idx) => idx);
  }

  // Cycle through proficiency levels: None -> Basic -> Good -> Excellent -> None
  cycleProficiencyLevel(categoryIndex: number, skillIndex: number): void {
    if (!this.isEditable) return;
    
    const skillItem = this.getSkillItems(categoryIndex).at(skillIndex) as FormGroup;
    const currentRatingId = skillItem.get('UserAnseredSkillRatingId')?.value || 0;
    
    // Cycle order: None (4) -> Basic (3) -> Good (2) -> Excellent (1) -> None (4)
    let nextRatingId: number;
    
    if (currentRatingId === 0 || currentRatingId === 4) {
      nextRatingId = 3; // None -> Basic
    } else if (currentRatingId === 3) {
      nextRatingId = 2; // Basic -> Good
    } else if (currentRatingId === 2) {
      nextRatingId = 1; // Good -> Excellent
    } else {
      nextRatingId = 4; // Excellent -> None
    }
    
    skillItem.patchValue({ UserAnseredSkillRatingId: nextRatingId });
  }

  buildDynamicForm(categories: any[]) {
    const categoryFGs = categories.map((category, index) => {
      const skillItems = category.SkillItems || [];
      const skillItemFGs = skillItems.map((item: any) => {
        return this.createSkillItemFG(item);
      });

      const categoryFG = this.fb.group({
        Id: [category.Id],
        Code: [category.Code],
        Name: [category.Name],
        HasYesNoToggle: [category.HasYesNoToggle],
        IsSelected: [category.IsSelected || false],
        SkillItems: this.fb.array(skillItemFGs)
      });

      // Special case: "GENERAL" and "KEY_ABILITIES" are enabled by default
      if (category.Code === 'GENERAL' || category.Code === 'KEY_ABILITIES') {
        categoryFG.patchValue({ IsSelected: true });
        // Expand GENERAL and KEY_ABILITIES by default
        this.expandedCategories.add(index);
        if (skillItems.length === 0) {
          this.fetchSkillItems(category.Id, categoryFG.get('SkillItems') as FormArray);
        }
      } else if (category.IsSelected) {
        // Expand categories that are already selected
        this.expandedCategories.add(index);
        if (skillItems.length === 0) {
          // If it was already selected but has no items, fetch them
          this.fetchSkillItems(category.Id, categoryFG.get('SkillItems') as FormArray);
        }
      }

      return categoryFG;
    });

    const categoryArray = this.fb.array(categoryFGs);
    this.skillsForm.setControl('Categories', categoryArray);
  }

  toggleCategory(index: number): void {
    if (this.expandedCategories.has(index)) {
      this.expandedCategories.delete(index);
    } else {
      this.expandedCategories.add(index);
    }
  }

  isCategoryExpanded(index: number): boolean {
    return this.expandedCategories.has(index);
  }

  fetchSkillItems(categoryId: number, skillItemsArray: FormArray) {
    this.api.apiCall(`Skills/GetSkillItemsByCategory?skillCategoryId=${categoryId}`, 'GET').subscribe((response: any) => {
      if (response.Data && response.Data.SkillItems) {
        // Clear existing items to ensure the list is always fresh from the database
        skillItemsArray.clear();
        response.Data.SkillItems.forEach((item: any) => {
          skillItemsArray.push(this.createSkillItemFG(item));
        });
      }
    });
  }

  onCategoryToggle(index: number, isSelected: boolean) {
    const categoryGroup = this.categories.at(index) as FormGroup;
    const categoryId = categoryGroup.get('Id')?.value;
    const skillItemsArray = categoryGroup.get('SkillItems') as FormArray;

    if (isSelected) {
      // Always fetch latest items from the database when "Yes" is clicked
      this.fetchSkillItems(categoryId, skillItemsArray);
      // Automatically expand the category when "Yes" is selected
      this.expandedCategories.add(index);
    } else {
      // Optionally collapse when "No" is selected
      // this.expandedCategories.delete(index);
    }
  }

  createSkillItemFG(item: any): FormGroup {
    return this.fb.group({
      Id: [item.Id],
      Name: [item.Name],
      HasSpecifyText: [item.HasSpecifyText],
      SpecifyLabel: [item.SpecifyLabel],
      UserAnseredSkillRatingId: [item.UserAnseredSkillRatingId || 0],
      UserAnseredSpecifyText: [item.UserAnseredSpecifyText || '']
    });
  }

  onSubmit(Mode: number): void {

    // this.skillsForm.patchValue({
    //   SaveMode: Mode
    // })

    if (!this.skillsForm.valid && Mode == this.SaveMode.SAVE) {
      this.submitted = true;
      this.skillsForm.markAllAsTouched();
      return
    }

    // Prepare data for API as per the new required format
    const formValue = this.skillsForm.value;
    const requestBody = {

        SkillCategories: formValue.Categories.map((category: any) => {
          return {
            SkillCategoryId: Number(category.Id),
            SkillCategoryName: category.Name,
            IsSelected: !!category.IsSelected,
            Skills: category.SkillItems.map((item: any) => {
              return {
                SkillItemId: Number(item.Id),
                SkillRatingId: item.UserAnseredSkillRatingId ? Number(item.UserAnseredSkillRatingId) : null,
                SpecifyText: item.UserAnseredSpecifyText || ''
              };
            })
          };
        })
  
    };




    this.addUpdate(requestBody, Mode);
  }


  addUpdate(reqBoby: any, Mode: number) {
    reqBoby.SaveMode = Mode;
    this.api.apiCall('Skills/SaveSkills', 'POST', reqBoby).subscribe((response: any) => {
      this.toastr.success(response?.Message);
      if (Mode == this.SaveMode.SAVE) {
        this.nextStep.emit();
      }
    });
  }






 
  goToBack(){
    this.previous.emit();
  }

  // Translations
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }



}
